import React from 'react';
import { Button } from 'primereact/button';
import {
    SP_SKIP, extractResponseData, SmartStyles, SmartHeader, SmartSection, NoRecordsCard
} from './_SmartFieldRenderer';

// The live payload names things `gstin` / `gstin_status` / `date_of_registration`
// / `nature_bus_activities` / `address`, not the `gstin_number` / `status` /
// `registration_date` / `nature_of_business_activities` /
// `principal_place_of_business` this list used to assume. None of those matched,
// so the GSTIN itself, its status, the dates and the address all dropped through
// to OTHER DETAILS. Both spellings are listed so either shape renders.
// Kept in step with GstinPdfController::SECTIONS.
const GROUPS = {
    business:     { title: 'BUSINESS DETAILS',   icon: 'pi-briefcase',   keys: ['gstin', 'gstin_number', 'business_name', 'legal_name', 'trade_name', 'pan_number', 'constitution_of_business', 'taxpayer_type'] },
    status:       { title: 'STATUS',             icon: 'pi-info-circle', keys: ['gstin_status', 'status', 'date_of_registration', 'registration_date', 'date_of_cancellation', 'cancellation_date', 'last_updated', 'gstin_type', 'einvoice_status'] },
    jurisdiction: { title: 'JURISDICTION',       icon: 'pi-map-marker',  keys: ['center_jurisdiction', 'state_jurisdiction'] },
    activities:   { title: 'NATURE OF BUSINESS', icon: 'pi-list',        keys: ['nature_bus_activities', 'nature_of_business_activities', 'nature_of_core_business_activity_description', 'nature_of_core_business_activity_code'] },
    principal:    { title: 'PRINCIPAL PLACE',    icon: 'pi-home',        keys: ['address', 'principal_place_of_business'] },
    compliance:   { title: 'COMPLIANCE',         icon: 'pi-verified',    keys: ['aadhaar_validation', 'aadhaar_validation_date', 'field_visit_conducted'] },
    filings:      { title: 'FILING STATUS',      icon: 'pi-file',        keys: ['filing_status', 'filing_frequency'] },
};

const HIGHLIGHT = ['gstin', 'gstin_number'];

// Surepass returns these placeholders for "never happened". Showing
// "Date of Cancellation: 1800-01-01" on an active registration reads as a data
// error to anyone reviewing the record.
const NULL_DATES = new Set(['1800-01-01', '0000-00-00']);

// `onDownloadPDF`/`pdfPasswordHint` are optional. The shared-link and lead
// history views render this component without them, and those readers have no
// business regenerating a billed report's PDF — so the button only appears when
// the host actually wires it up.
const GstinOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details. Please verify and try again." />;
    }

    const isPrintable = (v) => v !== null && v !== undefined && v !== '' && !NULL_DATES.has(v);

    const d = responseData;
    const grouped = {};
    for (const [groupName, group] of Object.entries(GROUPS)) {
        const picked = {};
        group.keys.forEach(k => { if (k in d && isPrintable(d[k])) picked[k] = d[k]; });
        if (Object.keys(picked).length > 0) grouped[groupName] = picked;
    }
    const knownKeys = new Set(Object.values(GROUPS).flatMap(g => g.keys));
    const leftover = {};
    for (const [k, v] of Object.entries(d)) {
        if (SP_SKIP.has(k) || knownKeys.has(k)) continue;
        if (!isPrintable(v)) continue;
        leftover[k] = v;
    }

    return (
        <div>
            <SmartStyles />
            <SmartHeader title="GSTIN Verification Report" icon="pi-verified" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />
            <div className="horizontal-report-card">
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button
                                label="Download PDF"
                                icon="pi pi-file-pdf"
                                size="small"
                                outlined
                                onClick={onDownloadPDF}
                            />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}
                {Object.entries(GROUPS).map(([groupName, meta]) => (
                    grouped[groupName]
                        ? <SmartSection key={groupName} title={meta.title} icon={meta.icon} data={grouped[groupName]} highlightKeys={HIGHLIGHT} />
                        : null
                ))}
                {Object.keys(leftover).length > 0 && (
                    <SmartSection title="OTHER DETAILS" icon="pi-info-circle" data={leftover} />
                )}
            </div>
        </div>
    );
};

export default GstinOutputComponent;
